import React, { useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { Editor } from 'auto-design-editor';
import * as api from 'utils/api';
import { useAppContext } from 'components/AppContext';
import { getFileUrl } from 'utils/file';
import EditorHeader from 'components/Common/EditorHeader';
import useTranslation from 'hooks/useTranslation';
import configs from 'configuration';
import { ModalKey } from 'constants/modal';
import { useModalContext } from 'components/ModalContext';
import AddCameraButton from './AddCameraButton';

export default function EditImage() {
  const { imageId } = useParams();
  const { logout, request } = useAppContext();
  const { tEditor } = useTranslation();
  const { showModal } = useModalContext();

  const onError = useCallback((error) => {
    console.error(error);
    if (error.code === 401) {
      logout();
    }
  }, [logout]);

  const editorProps = useMemo(() => ({
    onRender: async (data) => {
      const { success, error, result } = await request(api.render(imageId, data));

      if (success) {
        return result;
      }

      if (error.data?.errorCode === 'RENDER_LIMIT_REACHED') {
        return new Promise((resolve, reject) => {
          showModal(ModalKey.PURCHASE_EXTRA_RENDER, {
            imageId,
            onDone: async () => {
              const res = await request(api.render(imageId, data));

              if (res.success) {
                resolve(res.result);
              } else {
                reject(res.error);
              }
            },
            onCancel: () => {
              reject(error);
            },
          });
        });
      }

      throw error;
    },
    onGetImage: () => api.getImage(imageId),
    onUpdateImage: (data) => api.updateImage(imageId, data),
    onGetCategories: () => api.getCategories(),
    onGetItems: ({ after, limit, roomId, categoryId }) => api.getItems({
      after,
      limit,
      roomId,
      categoryId,
    }),
    onGetPersonalCollectionItems: (collectionId) => api.getPersonalCollectionItems(collectionId),
    onGetPersonalCollections: () => api.getPersonalCollections(),
    onGetRooms: () => api.getRooms(),
    onGetRoom: (roomCategoryId) => api.getRoom(roomCategoryId),
    onGetLocation: (locationId) => api.getLocation(locationId),
    onGetItemCategories: (itemId) => api.getItemCategories(itemId),
    onAddItemToPersonalCollections: (itemId, collectionIds) => api.addItemToPersonalCollections(itemId, collectionIds),
    onGetPersonalCollectionsOfAnItem: (itemId) => api.getPersonalCollectionsOfAnItem(itemId),
    onCreatePersonalCollection: (data) => api.createPersonalCollection(data),
    onEditPersonalCollection: (collectionId, data) => api.editPersonalCollection(collectionId, data),
    onDeletePersonalCollection: (collectionId) => api.deletePersonalCollection(collectionId),
    enablePersonalCollections: true,
    enableSameLocation: true,
    onError,
    getFileUrl,
    renderLogo: (className) => (
      <EditorHeader
        className={className}
        imageId={imageId}
        name={tEditor('3dEditor')}
      />
    ),
    enableRender: true,
    instructionUrl: configs.instructionUrl,
    renderAddCameraButton: ({ onAddCamera, numCameras }) => (
      <AddCameraButton onAddCamera={onAddCamera} numCameras={numCameras} imageId={imageId} />
    ),
  }), [imageId, onError, tEditor]);

  return (
    <Editor
      {...editorProps}
    />
  );
}
